import type { ChatCitation, LocalizedBotContent } from '@apaxir/shared';
import type { Translations } from '../i18n/translations';
import { icons } from './icons';

/**
 * Pure HTML string fragments. The component wires them into the shadow DOM
 * and attaches event listeners after insertion.
 *
 * User-supplied strings (message content, bot name) are escaped via
 * `escapeHtml` before interpolation — no innerHTML-based XSS vectors.
 */

export function escapeHtml(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function launcherHtml(t: Translations): string {
  return `<button class="launcher" part="launcher" aria-label="${escapeHtml(t.chatLauncher)}">${icons.chat}</button>`;
}

export function panelHtml(
  t: Translations,
  botContent: LocalizedBotContent,
  logoUrl?: string,
  maxMessageChars?: number,
): string {
  // logoUrl is rendered as a 28px circular avatar to the left of the bot's
  // name. Plain <img> with `loading="lazy"` and an explicit alt; if the URL
  // is bad the broken-image is hidden via the onerror handler so the header
  // gracefully degrades to text-only instead of showing a broken icon.
  const logo = logoUrl
    ? `<img class="header-logo" part="header-logo" src="${escapeHtml(logoUrl)}" alt="${escapeHtml(botContent.name)}" loading="lazy" onerror="this.style.display='none'" />`
    : '';
  // Phase 3f.1 — maxlength on the textarea is a UX hint (browser stops
  // accepting more typed chars). Server-side zod + chat.service are the
  // authoritative gates. Counter visibility is driven by the wrapper —
  // hidden by default, shown via JS once a few chars are typed.
  const maxlenAttr = maxMessageChars ? ` maxlength="${maxMessageChars}"` : '';
  const counter = maxMessageChars
    ? `<div class="char-counter" part="char-counter" aria-hidden="true" data-state="ok">
         <span class="char-counter__current">0</span>/<span class="char-counter__max">${maxMessageChars}</span>
       </div>`
    : '';
  return `
    <section class="panel" part="panel" role="dialog" aria-label="${escapeHtml(botContent.name)}">
      <header class="header" part="header">
        ${logo}
        <div class="header-title">${escapeHtml(botContent.name)}</div>
        <button class="header-btn" data-action="restart" aria-label="${escapeHtml(t.restartButton)}" title="${escapeHtml(t.restartButton)}">${icons.restart}</button>
        <button class="header-btn" data-action="close" aria-label="${escapeHtml(t.closeButton)}">${icons.close}</button>
      </header>
      <div class="messages" part="messages" role="log" aria-live="polite"></div>
      <div class="input-area" part="input-area">
        <div class="input-wrap">
          <textarea class="input" rows="1"${maxlenAttr} placeholder="${escapeHtml(t.inputPlaceholder)}" aria-label="${escapeHtml(t.inputPlaceholder)}"></textarea>
          ${counter}
        </div>
        <button class="send-btn" data-action="send" aria-label="${escapeHtml(t.sendButton)}">${icons.send}</button>
      </div>
    </section>
  `;
}

export type BubbleRole = 'user' | 'assistant' | 'error';

export function bubbleHtml(
  role: BubbleRole,
  content: string,
  citations?: ChatCitation[],
  t?: Translations,
): string {
  const cls =
    role === 'user'
      ? 'bubble bubble--user'
      : role === 'error'
        ? 'bubble bubble--error'
        : 'bubble bubble--assistant';
  const footer =
    role === 'assistant' && citations && citations.length > 0 && t
      ? citationsFooterHtml(citations, t)
      : '';
  return `<div class="${cls}">${escapeHtml(content)}${footer}</div>`;
}

/**
 * Phase 3d.1 — citations footer rendered inside the assistant bubble,
 * separated from the response text by a thin `<hr>`. Entries with a
 * `url` become anchors that open in a new tab; null-url entries are
 * label-only spans.
 */
export function citationsFooterHtml(
  citations: ChatCitation[],
  t: Translations,
): string {
  if (citations.length === 0) return '';
  const label = citations.length === 1 ? t.citationLabelSingular : t.citationsLabel;
  const items = citations
    .map((c) => {
      const safeLabel = escapeHtml(c.label);
      if (c.url) {
        const href = escapeHtml(c.url);
        return `<a class="bubble__citation-link" href="${href}" target="_blank" rel="noopener noreferrer" title="${href}">${safeLabel}</a>`;
      }
      return `<span class="bubble__citation-link">${safeLabel}</span>`;
    })
    .join('<span class="bubble__citation-sep"> · </span>');
  return `<hr class="bubble__citation-hr"><div class="bubble__citations"><span class="bubble__citation-label">${escapeHtml(label)}</span> ${items}</div>`;
}

export function suggestionHtml(text: string): string {
  return `<button class="suggestion" data-action="suggestion" data-prompt="${escapeHtml(text)}">${escapeHtml(text)}</button>`;
}

export function suggestionsContainerHtml(prompts: string[]): string {
  return `<div class="suggestions">${prompts.map(suggestionHtml).join('')}</div>`;
}

export function typingIndicatorHtml(t: Translations): string {
  return `<span class="typing" aria-label="${escapeHtml(t.typingIndicator)}"><span></span><span></span><span></span></span>`;
}

/**
 * Inline fallback shown when bot is unreachable or misconfigured. Muted
 * gray pill, non-interactive, same corner as the launcher would occupy.
 * Explicit role=status so screen readers announce the failure instead of
 * silently presenting nothing.
 */
export function errorStateHtml(message: string): string {
  return `<div class="error-state" role="status">${icons.close}<span>${escapeHtml(message)}</span></div>`;
}
